import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  applyMeeting,
  getMeetingById,
  getMeetings,
  updateMeetingAccount,
  updateMeetingStatus,
  type MeetingResource,
} from "@/services/frontendServices/meetingsService";
import type { MeetingDto, ListMeetingsResponse } from "@/types";
import { accountKeys } from "./useAccounts";

export const MEETINGS_PAGE_SIZE = 50;

export const meetingKeys = {
  all: (resource: MeetingResource) => [resource] as const,
  lists: (resource: MeetingResource) =>
    [...meetingKeys.all(resource), "list"] as const,
  list: (resource: MeetingResource, params?: unknown) =>
    [...meetingKeys.lists(resource), params ?? {}] as const,
  detail: (resource: MeetingResource, id: string) =>
    [...meetingKeys.all(resource), "detail", id] as const,
};

/** Unwrap the API's JSON error body, which arrives as the Error message. */
const describeError = (error: Error): string => {
  const message = error.message || "Unexpected error";
  try {
    return JSON.parse(message)?.error || message;
  } catch {
    return message;
  }
};

export function useMeeting(resource: MeetingResource, id: string) {
  return useQuery<MeetingDto, Error>({
    queryKey: meetingKeys.detail(resource, id),
    queryFn: () => getMeetingById(resource, id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useMeetings(
  resource: MeetingResource,
  params: {
    page?: number;
    status?: string | null;
    sortField?: string | null;
    sortDir?: "ASC" | "DESC";
  } = {}
) {
  const {
    page = 0,
    status = "review",
    sortField = null,
    sortDir = "DESC",
  } = params;
  const query = {
    page,
    pageSize: MEETINGS_PAGE_SIZE,
    status,
    sortField,
    sortDir,
  };

  return useQuery<ListMeetingsResponse, Error>({
    queryKey: meetingKeys.list(resource, query),
    queryFn: () => getMeetings(resource, query),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

/** Link an account onto the draft, then apply it — the review sheet's save action. */
export function useLinkAndApplyMeeting(resource: MeetingResource) {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingDto,
    Error,
    { id: string; linkedAccountId: string | null }
  >({
    mutationFn: async ({ id, linkedAccountId }) => {
      await updateMeetingAccount(resource, id, linkedAccountId);
      return applyMeeting(resource, id);
    },
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData<MeetingDto>(
        meetingKeys.detail(resource, id),
        updated
      );
      if (updated.normalizedData?.account?.existingAccountId) {
        queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      }
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(resource) });
      toast.success("Account linked", { duration: 3000 });
    },
    onError: (error) =>
      toast.error("Failed to apply data", {
        description: describeError(error),
        duration: 5000,
      }),
  });
}

export function useSetMeetingStatus(resource: MeetingResource) {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingDto,
    Error,
    { id: string; status: "review" | "archived" }
  >({
    mutationFn: ({ id, status }) => updateMeetingStatus(resource, id, status),
    onSuccess: (updated, { id, status }) => {
      queryClient.setQueryData<MeetingDto>(
        meetingKeys.detail(resource, id),
        updated
      );
      queryClient.invalidateQueries({ queryKey: meetingKeys.lists(resource) });
      toast.success(
        status === "archived" ? "Meeting archived" : "Meeting unarchived",
        {
          duration: 3000,
        }
      );
    },
    onError: (error) =>
      toast.error("Failed to update status", {
        description: describeError(error),
        duration: 5000,
      }),
  });
}
